import TextM from "@common/components/text/TextM"
import { theme } from "@common/theme/theme"
import { IS_WEB } from "@common/utils/mobileUtils"
import { useAudioWaveform } from "@livekit/components-react"
import * as PropTypes from "prop-types"
import React from "react"
import { View } from "react-native"
import FontAwesome5 from "react-native-vector-icons/FontAwesome5"
import FontAwesome6 from "react-native-vector-icons/FontAwesome6"

// A custom component to render the audio waveform
const AudioWaveform = ({ trackRef, size }) => {
    // Get the participant and find the audio track
    const participant = trackRef?.participant

    // Check if microphone is enabled and if participant is speaking
    const isMicEnabled = participant?.isMicrophoneEnabled
    const isSpeaking = participant?.isSpeaking

    // For web, we'll still try to use the real waveform
    let audioTrack = null
    if (IS_WEB && participant) {
        const publications = participant.getTrackPublications()
        const audioPublication = publications.find(
            (pub) => pub.kind === "audio"
        )
        audioTrack = audioPublication?.track
    }

    // State for animation on mobile
    const [animationPhase, setAnimationPhase] = React.useState(0)

    // Placeholder animation for mobile - only animate when speaking and mic enabled
    React.useEffect(() => {
        if (!IS_WEB && isMicEnabled && isSpeaking) {
            // Create animation cycle when participant is speaking
            const interval = setInterval(() => {
                setAnimationPhase((prev) => (prev + 1) % 12) // 12 phases for a smoother animation
            }, 150) // Update every 150ms for a subtle animation

            return () => clearInterval(interval)
        }
        return undefined
    }, [isMicEnabled, isSpeaking])

    // Only use waveform on web
    const waveform = IS_WEB
        ? useAudioWaveform(audioTrack, {
              barCount: 3,
              volMultiplier: 5,
              updateInterval: 100,
          })
        : null

    // Default bar patterns for different states
    const barCount = 3
    const baseHeights = [0.5, 0.5, 0.5] // Base pattern

    // Get animated heights for mobile or real waveform for web
    const getBarHeights = () => {
        // If mic is muted, return flat bars
        if (!isMicEnabled) {
            return [0.2, 0.2, 0.2].map((h) => h * size)
        }

        // For web with waveform data
        if (IS_WEB && waveform?.bars?.length > 0) {
            return waveform.bars.map((v) => Math.max(size * 0.3, v * size))
        }

        // For mobile, check if speaking
        if (!IS_WEB) {
            // If not speaking, return static bars
            if (!isSpeaking) {
                return baseHeights.map((h) => h * 0.4 * size) // Static but with microphone-on appearance
            }

            // Create sine-wave like animation patterns based on current phase
            // This creates a natural flowing animation between the bars
            return baseHeights.map((baseHeight, index) => {
                // Create phase offset for each bar to create a wave effect
                const phaseOffset = index * 2
                const phase = (animationPhase + phaseOffset) % 12

                // Sine-like pattern for natural animation
                const amplitude = 0.15 // How much the bars move
                const variation = Math.sin((phase * Math.PI) / 6) * amplitude

                // Apply the variation to base height
                const height = (baseHeight + variation) * size
                return height
            })
        }

        // Fallback
        return baseHeights.map((h) => h * size)
    }

    // Get the heights for current state
    const barHeights = getBarHeights()

    return (
        <View
            style={{
                flexDirection: "row",
                alignItems: "center",
                justifyContent: "center",
                width: size * 1.2,
                height: size,
            }}
        >
            {[...Array(barCount)].map((_, index) => {
                return (
                    <View
                        key={index}
                        style={{
                            width: 3,
                            height: barHeights[index],
                            backgroundColor: theme.colors.success,
                            marginHorizontal: 1,
                            borderRadius: 10,
                        }}
                    />
                )
            })}
        </View>
    )
}

export function UsernameComponent(props) {
    return (
        <View
            style={{
                position: "relative",
                maxWidth: "90%",
                ...props.style,
            }}
        >
            <View
                style={{
                    flexDirection: "row",
                    alignItems: "center",
                    justifyContent: "center",
                    alignSelf: "center",
                    backgroundColor: !props.soundMode && props.noDisplayName ? (theme.colors.purple + "dd") : theme.colors.blackTransparent,
                    paddingHorizontal: props.noDisplayName ? props.condition ? 2 : 6 : 4,
                    paddingVertical: 2,
                    borderRadius: 6,
                    borderWidth: 2,
                    borderColor: theme.colors.transparent,
                    ...props.speakerStyles,
                }}
            >
                {!props.noDisplayName && (
                    <TextM
                        color={theme.colors.white}
                        fontSize={10}
                        style={{
                            textAlign: "center",
                            marginRight: 5,
                            marginLeft: 2,
                        }}
                    >
                        {props.userObject?.display_name ??
                            props.rtcProps?.username}{" "}
                        <TextM color={theme.colors.white} fontSize={9}>
                            {props.isOffline && "| hors-ligne"}
                        </TextM>
                    </TextM>
                )}
                {!props.isOffline &&
                    (props.isScreenShare ? (
                        <FontAwesome6
                            name={"display"}
                            size={12}
                            color={theme.colors.white}
                        />
                    ) : (
                        <>
                            {props.condition ? (
                                <FontAwesome5
                                    name="microphone-slash"
                                    size={12}
                                    color={
                                        theme.colors.white
                                    }
                                />
                            ) : (
                                <AudioWaveform
                                    trackRef={props.trackRef}
                                    size={12}
                                />
                            )}
                        </>
                    ))}
            </View>
        </View>
    )
}

UsernameComponent.propTypes = {
    userObject: PropTypes.any,
    rtcProps: PropTypes.any,
    user: PropTypes.any,
    condition: PropTypes.any,
    speakerStyles: PropTypes.any,
    isScreenShare: PropTypes.bool,
    noDisplayName: PropTypes.bool,
    style: PropTypes.any,
    soundMode: PropTypes.any,
    miniMode: PropTypes.any,
    trackRef: PropTypes.object, // LiveKit track reference
}
